/**
 * #1476: the booking fee the member will actually be charged.
 *
 * The API adds 10% GST to the fee line item while the platform is
 * GST-registered (#819, `applyPlatformGst` in apps/api/src/tax/gst-pricing.ts),
 * so the Stripe page showed a higher fee and total than our checkout summary,
 * which mirrored only the pre-GST fee (4.9% + $0.99 per ticket). Every summary
 * that shows a fee runs its pre-GST figure through this so the two match to
 * the cent. Same rounding as the API's `gstOnExclusive`.
 */
export const GST_RATE_BPS = 1000

export function withPlatformGst(feeCents: number, platformGstRegistered: boolean): number {
  if (!platformGstRegistered || feeCents <= 0) return feeCents
  return feeCents + Math.round((feeCents * GST_RATE_BPS) / 10000)
}

export function bookingFeeLabel(platformGstRegistered: boolean): string {
  return platformGstRegistered ? "Booking fee (incl. GST)" : "Booking fee"
}

/**
 * #1480: a GST-registered PASS_ON facilitator's ticket is GST-EXCLUSIVE —
 * the API adds 10% on top (`computeTicketGst` in
 * apps/api/src/tax/gst-pricing.ts) and bases the fee on that GST-inclusive
 * value, so a checkout summary that priced tickets off the raw `price_cents`
 * undercharged itself relative to what Stripe actually billed. `GET
 * .../ticket-types` now returns `customer_price_cents` per ticket — what a
 * buyer pays for one, GST-inclusive; equal to `price_cents` for every other
 * row in the matrix (NOT_REGISTERED, or GST-registered ABSORB), so this is a
 * no-op there.
 */
export type GstTicket = {
  ticket_kind: "PAID" | "FREE" | "DONATION"
  price_cents: number
  customer_price_cents?: number | null
}

/** What one ticket actually costs the buyer, GST-inclusive. */
export function ticketCustomerPriceCents(ticket: GstTicket): number {
  return ticket.customer_price_cents ?? ticket.price_cents
}

/**
 * Does this event's facilitator pass ticket GST on to the buyer? Derived
 * from any PAID ticket whose customer price exceeds its advertised one —
 * true only for a GST-registered PASS_ON facilitator (the one row where the
 * two differ). Facilitator-level, so any PAID row answers it for all of them.
 */
export function eventHasPassOnTicketGst(tickets: GstTicket[]): boolean {
  return tickets.some(
    (t) => t.ticket_kind === "PAID" && ticketCustomerPriceCents(t) > t.price_cents,
  )
}

/**
 * Adds the facilitator's PASS_ON ticket GST to an arbitrary ex-GST amount —
 * a discounted subtotal, for instance, which `customer_price_cents` (a
 * per-ticket, undiscounted figure) cannot itself represent. Mirrors
 * `computeTicketGst`'s PASS_ON branch (`gstOnExclusive`). A no-op when the
 * facilitator doesn't pass GST on.
 */
export function withTicketGst(exGstCents: number, passOnGst: boolean): number {
  if (!passOnGst || exGstCents <= 0) return exGstCents
  return exGstCents + Math.round((exGstCents * GST_RATE_BPS) / 10000)
}

export function ticketPriceGstSuffix(passOnGst: boolean): string {
  return passOnGst ? " (incl. GST)" : ""
}

/**
 * What `qty` of this ticket cost the buyer in total, GST-inclusive — GST
 * applied once to the aggregate (`price_cents * qty`), the same way the
 * server's `computeTicketGst` runs once on the whole line. `qty *
 * ticketCustomerPriceCents(ticket)` (GST rounded per unit, then multiplied)
 * can differ by a cent or two at qty > 1 — e.g. a $19.95 PASS_ON-GST ticket
 * at qty 2 is $43.89 aggregate vs $43.90 per-unit-then-doubled (#1480 round 2).
 */
export function ticketLineTotalCents(ticket: GstTicket, qty: number, passOnGst: boolean): number {
  return withTicketGst(ticket.price_cents * qty, passOnGst)
}
